import React from 'react';

/**
 * Reusable Spinner Component
 *
 * A consistent loading spinner used throughout the application.
 * Supports different sizes and colors for various use cases.
 *
 * @param {string} size - Size variant: 'sm' (h-5 w-5), 'md' (h-8 w-8), 'lg' (h-12 w-12)
 * @param {string} color - Color variant: 'primary', 'white', 'red'
 * @param {string} className - Additional CSS classes
 */
export const Spinner = ({ size = 'md', color = 'primary', className = '' }) => {
  const sizeClasses = {
    sm: 'h-5 w-5',
    md: 'h-8 w-8',
    lg: 'h-12 w-12'
  };

  const colorClasses = {
    primary: 'border-primary-600 dark:border-primary-400',
    white: 'border-white',
    red: 'border-red-600'
  };

  return (
    <div
      className={`animate-spin rounded-full border-2 border-t-transparent ${sizeClasses[size]} ${colorClasses[color]} ${className}`}
      role="status"
      aria-label="Loading"
    />
  );
};

/**
 * Full-page Loading Screen
 *
 * Used for page-level loading states (e.g., initial data fetch, route loading).
 * Centers the spinner vertically and horizontally with an optional message.
 *
 * @param {string} message - Optional loading message to display
 */
export const LoadingScreen = ({ message = 'Loading...' }) => {
  return (
    <div className="flex justify-center items-center h-screen">
      <div className="text-center">
        <Spinner size="lg" />
        {message && (
          <p className="mt-4 text-gray-600 dark:text-gray-400">{message}</p>
        )}
      </div>
    </div>
  );
};

/**
 * Component Loading Container
 *
 * Used for component-level loading states within a page.
 * Provides consistent centering for inline loading states.
 * Centers both horizontally and vertically with minimum height.
 */
export const LoadingContainer = () => {
  return (
    <div className="flex justify-center items-center min-h-[200px]">
      <Spinner size="md" />
    </div>
  );
};

/**
 * Button Spinner
 *
 * Used inside buttons during form submission or async actions.
 * Automatically centers within the button and uses appropriate sizing.
 *
 * @param {string} color - Color variant: 'white' (default for primary buttons), 'red'
 */
export const ButtonSpinner = ({ color = 'white' }) => {
  return <Spinner size="sm" color={color} className="mx-auto" />;
};

/**
 * Inline Action Spinner
 *
 * Used for inline loading states (e.g., delete button, icon replacement).
 * Smaller size suitable for replacing icons or small UI elements.
 *
 * @param {string} color - Color variant: 'primary', 'white', 'red'
 */
export const InlineSpinner = ({ color = 'primary' }) => {
  return <Spinner size="sm" color={color} />;
};
